import { ChartInfoKind } from '@frontend/chart/types';
import { CommonMessage } from '@frontend/lang';
import { FormattedMessage, useIntl } from 'react-intl';

import { InsightCardWidth } from '../insight-card-width';
import { InsightCard } from '../insight-card/insight-card.component';
import { InsightId } from '../insight-registry/insight-ids';
import { useVendingTimeToStockoutList } from './use-vending-time-to-stockout-list';

export interface VendingTimeToStockoutListProps {
    accountId: string;
    width?: InsightCardWidth;
}

export const VendingTimeToStockoutList = ({ accountId, width }: VendingTimeToStockoutListProps) => {
    const { formatMessage } = useIntl();
    const { items, isLoading } = useVendingTimeToStockoutList(accountId);

    const daysHeader = formatMessage({
        id: 'platform.analytics.insights.vending-time-to-stockout-list.days',
        description: 'Column header for predicted days until empty on the vending time-to-stockout insights list',
        defaultMessage: 'Days left'
    });

    return (
        <InsightCard
            insightId={InsightId.VENDING_TIME_TO_STOCKOUT}
            infoKind={ChartInfoKind.VENDING_TIME_TO_STOCKOUT_LIST}
            width={width}
            title={
                <FormattedMessage
                    id='platform.analytics.insights.vending-time-to-stockout-list.title'
                    description='Title for the vending list showing expected days until products run out of stock on analytics insights'
                    defaultMessage='Expected out of stock in days'
                />
            }
            subtitle={
                <FormattedMessage
                    id='platform.analytics.insights.vending-time-to-stockout-list.subtitle'
                    description='Subtitle indicating time-to-stockout uses the last 30 days of consumption run-rate'
                    defaultMessage='last 30 days run-rate'
                />
            }
            isLoading={isLoading}>
            {items.length === 0 ? (
                <div className='text-foreground-default'>{CommonMessage.ERRORS.NO_DATA_AVAILABLE}</div>
            ) : (
                <div className='w-full min-w-0'>
                    <div className='border-border-default text-foreground-default flex items-start gap-4 border-b text-base font-medium'>
                        <div className='w-full px-1 py-2'>{CommonMessage.OBJECTS.PRODUCT.DEFAULT}</div>
                        <div className='w-[100px] shrink-0 py-2 text-right'>{daysHeader}</div>
                    </div>
                    {items.map((item) => (
                        <div
                            key={item.productKey}
                            className='border-border-default flex h-11 items-center gap-4 border-b'>
                            <div className='text-foreground-default flex w-full min-w-0 items-center px-1 text-sm font-medium'>{item.productName}</div>
                            <div className='flex w-[100px] shrink-0 justify-end'>
                                <span className='text-background-surface bg-status-warning inline-flex min-w-8 items-center justify-center rounded-full px-2 py-0.5 text-sm font-semibold'>
                                    {item.daysUntilEmpty}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </InsightCard>
    );
};
